import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/UI/LoadingSpinner';

interface ResetPasswordForm {
  new_password: string;
  confirm_password: string;
}

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { confirmPasswordReset } = useAuth();
  const [loading, setLoading] = useState(false);
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setError,
  } = useForm<ResetPasswordForm>();

  // If token missing, show error state
  useEffect(() => {
    if (!token) {
      setError('new_password', { type: 'manual', message: 'Token de redefinição ausente. Solicite um novo link.' });
    }
  }, [token, setError]);

  const onSubmit = async (data: ResetPasswordForm) => {
    if (!token) return;
    if (data.new_password !== data.confirm_password) {
      setError('confirm_password', { type: 'validate', message: 'As senhas não coincidem' });
      return;
    }

    setLoading(true);
    try {
      const ok = await confirmPasswordReset(token, data.new_password);
      if (ok) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const newPasswordValue = watch('new_password');

  return (
    <div className='min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8'>
      <div className='max-w-md w-full space-y-8'>
        <div>
          <h2 className='mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-gray-100'>Redefinir senha</h2>
          <p className='mt-2 text-center text-sm text-gray-600 dark:text-gray-400'>
            {token ? 'Digite sua nova senha abaixo.' : 'Token inválido ou ausente. Solicite um novo link.'}
          </p>
        </div>

        <form className='mt-8 space-y-6' onSubmit={handleSubmit(onSubmit)}>
          <div className='rounded-md shadow-sm -space-y-px'>
            <div className='mb-4'>
              <label htmlFor='new_password' className='sr-only'>
                Nova senha
              </label>
              <input
                id='new_password'
                type='password'
                autoComplete='new-password'
                className='appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 rounded-t-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm'
                placeholder='Nova senha'
                {...register('new_password', {
                  required: 'Nova senha é obrigatória',
                  minLength: { value: 6, message: 'A senha deve ter pelo menos 6 caracteres' },
                })}
              />
              {errors.new_password && <p className='mt-1 text-sm text-red-600'>{errors.new_password.message}</p>}
            </div>

            <div>
              <label htmlFor='confirm_password' className='sr-only'>
                Confirmar nova senha
              </label>
              <input
                id='confirm_password'
                type='password'
                autoComplete='new-password'
                className='appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 rounded-b-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm'
                placeholder='Confirmar nova senha'
                {...register('confirm_password', {
                  required: 'Confirmação de senha é obrigatória',
                  validate: value => value === newPasswordValue || 'As senhas não coincidem',
                })}
              />
              {errors.confirm_password && (
                <p className='mt-1 text-sm text-red-600'>{errors.confirm_password.message}</p>
              )}
            </div>
          </div>

          <div>
            <button
              type='submit'
              disabled={loading || !token}
              className='group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50'
            >
              {loading ? <LoadingSpinner size='sm' className='mr-2' /> : null}
              {loading ? 'Redefinindo...' : 'Redefinir senha'}
            </button>
          </div>

          <div className='text-center'>
            <p className='text-sm text-gray-600 dark:text-gray-400'>
              Lembrou a senha?{' '}
              <Link
                to='/login'
                className='font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300'
              >
                Faça login
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
